import { JSX, useContext, useEffect, useState } from "react";
import { ClaimsActivityGhostProviderScenario } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { PciDashboardScenarioTypeEnum, PciDashboardSummaryType } from "@app/projects/network-assessment/pci-dashboard/shared";
import { getPciDashboardScenarioChangeDisplay, getScenarioDisplay } from "@app/projects/network-assessment/pci-dashboard/shared/utility";
import { ColumnConfig, printNumberAsString, SortDirections } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { resizeObserverManager } from "@app/shared/qes-standards/utility/ResizeObserverManager";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";
import { EllipsisTooltip } from "@app/shared/EllipsisTooltip";
import { formatDateTime } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";
import { PciDashboardFiltersPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersPayload";
import { ClaimsActivityGhostProviderScenarioModel } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityGhostProviderScenarioModel";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersContext";

interface ClaimsActivityGhostProviderScenarioTileProps {
  loading: boolean;
  getSummaries: (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string,
    sortDirection: SortDirection,
    isSorting?: boolean,
    isFiltering?: boolean,
  ) => void;
  onTopGhostDashboardDetailsViewClick: (
    dashboardDetailsParam: string,
    summaryType: PciDashboardSummaryType,
    scenario?: PciDashboardScenarioTypeEnum,
  ) => void;
  scenarioModel: ClaimsActivityGhostProviderScenarioModel;
  summaryType: PciDashboardSummaryType;
}

interface ClaimsActivityGhostProviderScenarioToppings {
  changeCount: JSX.Element;
  compareGhostCount: string | JSX.Element;
  description: string;
  ghostCount: string | JSX.Element;
  name: JSX.Element;
  key: string;
}

const ClaimsActivityGhostProviderScenarioTile = (props: ClaimsActivityGhostProviderScenarioTileProps): JSX.Element => {
  const [buildingRecords, setBuildingRecords] = useState<boolean>(false);
  const [loadAllClicked, setLoadAllClicked] = useState<boolean>(false);
  const [ghostProviderScenarios, setGhostProviderScenarios] = useState<ClaimsActivityGhostProviderScenarioToppings[]>([]);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [sortedColumn, setSortedColumn] = useState<string>(null);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);

  useEffect(() => {
    if (!!props.scenarioModel?.records) {
      const waffleToppings: ClaimsActivityGhostProviderScenarioToppings[] = props.scenarioModel?.records.map((gs, index) => {
        /* oxlint-disable sort-keys */
        return {
          key: `${gs.name}-${index}`,
          name: (
            <EllipsisTooltip
              key={index}
              text={gs.name}
            />
          ),
          description: gs.description,
          ghostCount: getCountDisplay(gs, "ghostCount", index),
          compareGhostCount: getCountDisplay(gs, "compareGhostCount", index),
          changeCount: getPciDashboardScenarioChangeDisplay(gs.changeCount),
        };
        /* oxlint-enable sort-keys */
      });

      setGhostProviderScenarios(waffleToppings);
    }
  }, [props.scenarioModel, pciDashboardFiltersContext.displayedFilters]);

  useEffect(() => {
    const resizeCallback = (event: ResizeObserverEntry): void => {
      // when sorting or loading in a large amount of records,
      // there could be some time between when the api call is complete
      // and when the records are visibly displayed
      const resizeEntry = event?.target?.children;
      const tbodyElement = [...resizeEntry]?.find(x => x.localName === "tbody");

      if (tbodyElement?.childElementCount === ghostProviderScenarios?.length && buildingRecords) {
        setLoadAllClicked(false);
        setBuildingRecords(false);
      }
    }

    const wafflesTable = document.querySelector(`[data-testid=claims-activity__${props.summaryType}-ghost-scenario--table]`);

    if (!!wafflesTable) {
      resizeObserverManager.observe(wafflesTable, resizeCallback);

      return () => {
        resizeObserverManager.unobserve(wafflesTable);
      }
    }
  }, [ghostProviderScenarios]);

  useEffect(() => {
    if (props.loading && !!ghostProviderScenarios.length) {
      setGhostProviderScenarios([]);
    }
  }, [props.loading]);

  const columnConfig: ColumnConfig = {
    changeCount: {
      alignment: TextAlignment.Center,
      displayElement: <><br />Change</>,
      sizeContentToFit: true,
      testId: "change-count",
    },
    compareGhostCount: {
      alignment: TextAlignment.Center,
      displayElement: <>{getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)}<br />Count</>,
      sizeContentToFit: true,
      testId: "compare-ghost-count",
    },
    description: {
      alignment: TextAlignment.Center,
      displayElement: <><br />{props.summaryType === PciDashboardSummaryType.County ? "State" : "Code"}</>,
      sizeContentToFit: true,
      testId: "description",
    },
    ghostCount: {
      alignment: TextAlignment.Center,
      displayElement: <>Latest Timeslice<br />Count</>,
      sizeContentToFit: true,
      testId: "ghost-count",
    },
    name: {
      alignment: TextAlignment.Left,
      displayElement: <><br />{props.summaryType}</>,
      sizeContentToFit: true,
      testId: "name",
    },
  };

  const customSort = (
    records: ClaimsActivityGhostProviderScenarioToppings[],
    column: keyof ClaimsActivityGhostProviderScenarioToppings,
    sortedDirection: SortDirections
  ): ClaimsActivityGhostProviderScenarioToppings[] => {
    let direction: SortDirection = null;

    switch (sortedDirection) {
      case SortDirections.Asc:
        direction = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        direction = SortDirection.Descending;
        break;
      case SortDirections.None:
        direction = SortDirection.None;
        break;
      default:
        direction = SortDirection.Unknown;
        break;
    }

    setSortDirection(direction);
    setSortedColumn(column);
    setBuildingRecords(true);

    props.getSummaries(null, props.summaryType, column, direction, true);

    return records;
  }

  const getCountDisplay = (providerScenario: ClaimsActivityGhostProviderScenario, key: keyof ClaimsActivityGhostProviderScenario, index: number) => {
    if (!providerScenario[key]) {
      return "---";
    }

    return (
      <QESButton
        onClick={() => props.onTopGhostDashboardDetailsViewClick(
          props.summaryType === PciDashboardSummaryType.County ? providerScenario.name : providerScenario.description,
          props.summaryType,
          key === "compareGhostCount" ? pciDashboardFiltersContext.displayedFilters.compareScenario : PciDashboardScenarioTypeEnum.Original
        )}
        qesTestId={`claims-activity__${props.summaryType}-ghost-scenario--dashboard-details-${index}`}
        qesType="secondary"
      >
        {printNumberAsString(providerScenario[key] as number, "0")}
      </QESButton>
    );
  }

  const onLoadAllClick = () => {
    setLoadAllClicked(true);
    setBuildingRecords(true);
    props.getSummaries(null, props.summaryType, sortedColumn, sortDirection);
  }

  return (
    <div className="col-md-6 p0">
      <DashboardTile
        classNames="pci-dashboard__summary-tile dashboard__scrollable"
        loading={props.loading}
        title={`Ghost Providers by ${props.summaryType} - Network Providers`}
        subtitle={`Comparison of ${formatDateTime(pciDashboardFiltersContext.projectRunConfiguration?.dateCreated)} and ${getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)}`}
        widthClass=""
      >
        <Waffles<ClaimsActivityGhostProviderScenarioToppings>
          columns={columnConfig}
          customSort={customSort}
          data={ghostProviderScenarios}
          fullWidth={true}
          passThroughSorting={true}
          testId={`claims-activity__${props.summaryType}-ghost-scenario--table`}
          theme={WafflesThemes.Simple}
        />

        {buildingRecords ? (
          <div className="d-flex justify-content-center mt-3">
            <LoadingSpinner />
          </div>
        ) : (
          (props.scenarioModel?.totalCount > ghostProviderScenarios?.length && !loadAllClicked) && (
            <div className="d-flex justify-content-center mt-3">
              <QESButton
                qesTestId={`claims-activity__${props.summaryType}-ghost-scenario--load-all`}
                qesType="primary"
                onClick={() => onLoadAllClick()}
              >
                Load All
              </QESButton>
            </div>
          )
        )}
      </DashboardTile>
    </div>
  )
}

export default ClaimsActivityGhostProviderScenarioTile;